import { useRef } from "react";
import { Input } from "@/components/ui/input";
import { isValidFrequency, normalizeFrequency, autoFormatFrequency } from "@emr/shared";

interface Props {
  value: string;
  onChange: (v: string) => void;
  onCommit?: () => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

/**
 * Smart free-text frequency input. Accepts hyphen-separated dose segments
 * ("1-1-1", "1.5-0-1", "1-1-1-1-1-1-1"). As the user types, hyphens are
 * inserted automatically between segments: typing "1111" yields "1-1-1-1"
 * and typing "1.51.51.51.5" yields "1.5-1.5-1.5-1.5" (see autoFormatFrequency
 * in @emr/shared). Blank is allowed (frequency can be empty when instructions
 * carry the meaning). Invalid characters are ignored. Enter commits the field.
 */
export function FrequencyInput({ value, onChange, onCommit, placeholder = "1-1-1", className, id, disabled }: Props) {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <Input
      id={id}
      ref={ref}
      inputMode="decimal"
      disabled={disabled}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        // Auto-format: hyphens are inserted between dose segments as the
        // user types. autoFormatFrequency strips non-[digit/dot/hyphen]
        // chars, collapses repeats, and inserts separators per the grammar
        // (each whole digit is its own segment; a digit + dot + 1 decimal
        // forms a decimal segment like "1.5"). A trailing hyphen is
        // preserved so the cursor flows into the next segment; it is
        // stripped by normalizeFrequency on blur/commit.
        onChange(autoFormatFrequency(e.target.value));
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (onCommit) onCommit();
        }
      }}
      onBlur={() => onChange(normalizeFrequency(value))}
    />
  );
}
